import { useState, useEffect } from 'react';
import { usersAPI, rolesAPI, tenantsAPI } from '../services/api';
import { Users as UsersIcon, Plus, Edit2, Trash2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

function UserModal({ user, roles, tenants, onClose, onSaved }) {
  const [form, setForm] = useState(user ? {
    full_name: user.full_name, status: user.status,
    tenant_id: user.tenant_id || '', role_ids: user.roles?.map(r => r.id) || []
  } : { email: '', full_name: '', password: '', tenant_id: '', role_ids: [] });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleRole = (id) => {
    setForm(f => ({ ...f, role_ids: f.role_ids.includes(id) ? f.role_ids.filter(r => r !== id) : [...f.role_ids, id] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, tenant_id: form.tenant_id || null };
      if (user) await usersAPI.update(user.id, data);
      else await usersAPI.create(data);
      toast.success(user ? 'User updated' : 'User created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving user');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{user ? 'Edit User' : 'New User'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" value={form.full_name} required onChange={e => set('full_name', e.target.value)} placeholder="John Smith" />
          </div>
          {!user && <>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} required onChange={e => set('email', e.target.value)} placeholder="john@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} required onChange={e => set('password', e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number" />
            </div>
          </>}
          <div className="form-group">
            <label className="form-label">Brand (optional)</label>
            <select className="form-select" value={form.tenant_id} onChange={e => set('tenant_id', e.target.value)}>
              <option value="">Platform-wide (no brand restriction)</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {user && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Roles</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {roles.map((r, i) => {
                // Unique per row, so the label can never resolve to another role's input
                // even if the API hands back roles with duplicate or missing ids.
                const checkboxId = `user-role-${i}`;
                const selected = form.role_ids.includes(r.id);
                return (
                  <label key={checkboxId} htmlFor={checkboxId} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 6, background: selected ? 'var(--accent-glow)' : 'var(--bg-elevated)', border: `1px solid ${selected ? 'rgba(108,99,255,0.4)' : 'var(--border)'}`, transition: 'all 0.15s' }}>
                    <input id={checkboxId} type="checkbox" checked={selected} onChange={() => toggleRole(r.id)} style={{ accentColor: 'var(--accent)' }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                      {r.requires_mfa && <div style={{ fontSize: 11, color: 'var(--yellow)' }}>⚠ Requires MFA</div>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : user ? 'Save' : 'Create User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([usersAPI.list(), rolesAPI.list(), tenantsAPI.list()])
      .then(([u, r, t]) => { setUsers(u.data); setRoles(r.data); setTenants(t.data); })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await usersAPI.delete(id); toast.success('User deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage admin accounts and role assignments</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={16} /> New User</button>
      </div>

      <div className="card">
        {loading ? <div className="loading-state"><div className="spinner" /></div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Brand</th><th>Roles</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{u.email}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{tenants.find(t => t.id === u.tenant_id)?.name || <span style={{ color: 'var(--text-muted)' }}>Platform</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.roles?.map(r => <span key={r.id} className="badge badge-purple"><Shield size={9} />{r.name}</span>)}
                      </div>
                    </td>
                    <td><span className={`badge badge-${u.status === 'active' ? 'green' : u.status === 'suspended' ? 'red' : 'grey'}`}>{u.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setModal(u)}><Edit2 size={12} /> Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <UserModal user={modal === 'new' ? null : modal} roles={roles} tenants={tenants} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
